import React, { useState, useEffect } from 'react';
import {
    Modal,
    Button,
    TextInput,
    Box,
    Text,
    Group,
    Table,
    Paper,
    Checkbox,
    Badge,
    Tooltip,
} from '@mantine/core';
import { IconSearch, IconCheck } from '@tabler/icons-react';
import { MODAL_SIZES, TABLE_SCROLL_MIN_WIDTH } from '../../theme/mantineTheme';
import { EmptyState } from '../common/EmptyState';
import { entityService } from '../../services/entityServiceSingleton';
import { debugLog } from '../../utils/debugLog';
import { DialogErrorAlert } from './DialogErrorAlert';
import { LoadingState } from '../common/LoadingState';

interface FunctionCatalogItem {
    Id: string;
    DisplayName?: string;
    Inputs?: any[];
    TargetEntities?: string[];
    Registered?: boolean;
}

interface FunctionCatalogBrowserDialogProps {
    open: boolean;
    onClose: () => void;
    onConfirm: (selectedIds: string[]) => void;
    title?: string;
    /** Shown in a tooltip on the dialog title so you can trace which control opened this dialog. */
    callerName: string;
    singleSelection?: boolean;
    preloadedItems?: FunctionCatalogItem[];
}

const FunctionCatalogBrowserDialog: React.FC<FunctionCatalogBrowserDialogProps> = ({
    open,
    onClose,
    onConfirm,
    title = "Select Function Catalog Item",
    callerName,
    singleSelection = true,
    preloadedItems
}) => {
    const [items, setItems] = useState<FunctionCatalogItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [filteredItems, setFilteredItems] = useState<FunctionCatalogItem[]>([]);

    // Load function catalog items
    useEffect(() => {
        if (!open) {
            setItems([]);
            setSelectedIds([]);
            setSearchQuery('');
            return;
        }

        const loadItems = async () => {
            if (preloadedItems) {
                setItems(preloadedItems);
                setFilteredItems(preloadedItems);
                return;
            }

            setLoading(true);
            setError(null);

            try {
                const catalogItems = await entityService.getFunctionCatalog();
                setItems(catalogItems);
                setFilteredItems(catalogItems);
            } catch (err) {
                console.error('Error loading function catalog:', err);
                setError('Failed to load function catalog items');
            } finally {
                setLoading(false);
            }
        };

        loadItems();
    }, [open, entityService, preloadedItems]);

    // Filter items based on search query
    useEffect(() => {
        if (!searchQuery.trim()) {
            setFilteredItems(items);
            return;
        }

        const query = searchQuery.toLowerCase();
        const filtered = items.filter(item => {
            const displayName = (item.DisplayName || '').toLowerCase();
            const id = (item.Id || '').toLowerCase();
            return displayName.includes(query) || id.includes(query);
        });
        setFilteredItems(filtered);
    }, [searchQuery, items]);

    const handleClose = () => {
        setSelectedIds([]);
        setSearchQuery('');
        onClose();
    };

    const handleConfirm = () => {
        debugLog('[FunctionCatalogBrowserDialog] selected value passed to onConfirm:', selectedIds);
        onConfirm(selectedIds);
        handleClose();
    };

    const handleToggleSelection = (itemId: string) => {
        if (singleSelection) {
            setSelectedIds([itemId]);
        } else {
            setSelectedIds(prev => {
                if (prev.includes(itemId)) {
                    return prev.filter(id => id !== itemId);
                } else {
                    return [...prev, itemId];
                }
            });
        }
    };

    const isSelected = (itemId: string) => selectedIds.includes(itemId);

    const getDisplayName = (item: FunctionCatalogItem) => {
        return item.DisplayName || item.Id || 'N/A';
    };

    return (
        <Modal
            opened={open}
            onClose={handleClose}
            size={MODAL_SIZES.detail}
            title={
                <Tooltip label={`FunctionCatalogBrowserDialog - ${callerName}`} withArrow>
                    <Text component="span" fw={600} size="lg" style={{ cursor: 'help' }}>{title}</Text>
                </Tooltip>
            }
            styles={{
                body: { maxHeight: '80vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' },
                content: { maxHeight: '80vh' }
            }}
        >
            <Box mb="md">
                <TextInput
                    placeholder="Search by name or ID..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    leftSection={<IconSearch size={16} />}
                />
            </Box>

            {loading ? (
                <LoadingState variant="centered" />
            ) : error ? (
                <DialogErrorAlert error={error} />
            ) : filteredItems.length === 0 ? (
                <EmptyState
                    title={searchQuery ? 'No items found matching your search' : 'No function catalog items available'}
                />
            ) : (
                <Paper withBorder p="sm" mah="50vh" style={{ overflow: 'auto' }}>
                    <Table.ScrollContainer minWidth={TABLE_SCROLL_MIN_WIDTH}>
                    <Table stickyHeader highlightOnHover>
                        <Table.Thead>
                            <Table.Tr>
                                <Table.Th w={50}></Table.Th>
                                <Table.Th><Text fw={600} size="sm">Display Name</Text></Table.Th>
                                <Table.Th><Text fw={600} size="sm">ID</Text></Table.Th>
                                <Table.Th><Text fw={600} size="sm">Target Entities</Text></Table.Th>
                                <Table.Th><Text fw={600} size="sm">Registered</Text></Table.Th>
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {filteredItems.map((item) => (
                                <Table.Tr
                                    key={item.Id}
                                    onClick={() => handleToggleSelection(item.Id)}
                                    style={{
                                        cursor: 'pointer',
                                        backgroundColor: isSelected(item.Id) ? 'var(--mantine-color-blue-1)' : undefined
                                    }}
                                >
                                    <Table.Td onClick={(e) => e.stopPropagation()}>
                                        <Checkbox
                                            checked={isSelected(item.Id)}
                                            onChange={() => handleToggleSelection(item.Id)}
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                    </Table.Td>
                                    <Table.Td>
                                        <Text size="sm" fw={500}>
                                            {getDisplayName(item)}
                                        </Text>
                                    </Table.Td>
                                    <Table.Td>
                                        <Text size="xs" ff="monospace">
                                            {item.Id}
                                        </Text>
                                    </Table.Td>
                                    <Table.Td>
                                        {item.TargetEntities && item.TargetEntities.length > 0 ? (
                                            <Group gap={4} wrap="wrap">
                                                {item.TargetEntities.map((entity, idx) => (
                                                    <Badge key={idx} size="sm" variant="light">
                                                        {entity}
                                                    </Badge>
                                                ))}
                                            </Group>
                                        ) : (
                                            <Text size="sm" c="dimmed">
                                                N/A
                                            </Text>
                                        )}
                                    </Table.Td>
                                    <Table.Td>
                                        <Badge
                                            size="sm"
                                            color={item.Registered ? 'green' : 'gray'}
                                            variant="light"
                                        >
                                            {item.Registered ? 'Yes' : 'No'}
                                        </Badge>
                                    </Table.Td>
                                </Table.Tr>
                            ))}
                        </Table.Tbody>
                    </Table>
                    </Table.ScrollContainer>
                </Paper>
            )}

            {selectedIds.length > 0 && (
                <Box mt="md">
                    <Text size="sm" c="dimmed">
                        {singleSelection ? (
                            <>Selected: {getDisplayName(items.find(item => item.Id === selectedIds[0]) || { Id: selectedIds[0] })}</>
                        ) : (
                            <>Selected {selectedIds.length} item{selectedIds.length !== 1 ? 's' : ''}</>
                        )}
                    </Text>
                </Box>
            )}

            <Group justify="flex-end" mt="md">
                <Button variant="default" onClick={handleClose}>
                    Cancel
                </Button>
                <Button
                    variant="filled"
                    onClick={handleConfirm}
                    disabled={selectedIds.length === 0}
                    leftSection={<IconCheck size={16} />}
                >
                    {singleSelection ? 'Select' : `Select ${selectedIds.length > 0 ? `(${selectedIds.length})` : ''}`}
                </Button>
            </Group>
        </Modal>
    );
};

export default FunctionCatalogBrowserDialog;
